import { useCallback } from 'react';

import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { fireErrorAnalytics, fireSiteOptedOutAnalytics } from '../analytics/index.tsx';
import { isSiteOptedOut } from '../site-opt-out/index.tsx';
import { isSite7DaysOld } from '../site-eligibility/index.tsx';
import { isNewActiveUser } from '../user-eligibility/index.tsx';
import { hasProductAccess } from '../permission-check/index.tsx';
import { fetchIsRequestAccessCapable } from '../request-capabilities/index.tsx';
import { useOverrides } from '../overrides/index.tsx';
import { withEligibilityCache } from '../caching/index.tsx';
import type { EligibilityCheck } from './types.tsx';

/*
  Eligibility can be overriden using the following query parameter:

  ?cjn_jpd_overrides={
    "is_jpd_route": false,
    "site_has_no_licensed_jpd": false,
    "site_is_opted_out": false,
    "user_already_has_jpd": false,
    "user_cannot_request_jpd_access": false,
    "jpd_is_too_new": false,
    "user_is_too_new": false,
  }
*/
type OverrideKey =
	| 'is_jpd_route'
	| 'site_has_no_licensed_jpd'
	| 'site_is_opted_out'
	| 'user_already_has_jpd'
	| 'user_cannot_request_jpd_access'
	| 'jpd_is_too_new'
	| 'user_is_too_new';

export const useMsrJpdCrossJoinNudgeEligibility = () => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const licensedProducts = useLicensedProducts();
	const isJpdRoute = useIsActiveRoute('polaris-ideas');
	const withOverrides = useOverrides<OverrideKey>('cjn_jpd_overrides');

	return useCallback(async (): Promise<EligibilityCheck> => {
		try {
			// TODO: PUMA-423 replace with smart caching to further optimize calls
			const cachedIsSiteOptedOut = withEligibilityCache('site_is_opted_out', isSiteOptedOut, 0.2);
			const cachedHasProductAccess = withEligibilityCache(
				'user_already_has_jpd',
				hasProductAccess,
				30,
			);
			const cachedUserCanRequestAccess = withEligibilityCache(
				'user_cannot_request_jpd_access',
				fetchIsRequestAccessCapable,
				7,
			);

			// TODO: PUMA-423 replace with smart caching to further optimize calls
			const cachedJpdTooNew = withEligibilityCache(
				'jpd_is_too_new',
				async () => !(await isSite7DaysOld('jira-product-discovery', cloudId)),
				7,
			);

			// TODO: PUMA-423 replace with smart caching to further optimize calls
			const cachedUserTooNew = withEligibilityCache('user_is_too_new', isNewActiveUser, 7);
			if (withOverrides('is_jpd_route', isJpdRoute)) {
				return { isEligible: false, reason: 'is_jpd_route' };
			}

			if (withOverrides('site_has_no_licensed_jpd', !licensedProducts['jira-product-discovery'])) {
				return {
					isEligible: false,
					reason: 'site_has_no_licensed_jpd',
				};
			}

			if (
				withOverrides(
					'user_already_has_jpd',
					await cachedHasProductAccess('jira-product-discovery', cloudId),
				)
			) {
				return {
					isEligible: false,
					reason: 'user_already_has_jpd',
				};
			}

			const userCanRequestAccessToJpd = await cachedUserCanRequestAccess(
				'jira-product-discovery',
				cloudId,
			);
			if (withOverrides('user_cannot_request_jpd_access', !userCanRequestAccessToJpd)) {
				return {
					isEligible: false,
					reason: 'user_cannot_request_jpd_access',
				};
			}

			if (withOverrides('site_is_opted_out', await cachedIsSiteOptedOut(cloudId))) {
				fireSiteOptedOutAnalytics(createAnalyticsEvent, 'crossJoinNudgesJiraToJpd');
				return {
					isEligible: false,
					reason: 'site_is_opted_out',
				};
			}

			if (withOverrides('jpd_is_too_new', await cachedJpdTooNew())) {
				return {
					isEligible: false,
					reason: 'jpd_is_too_new',
				};
			}

			if (withOverrides('user_is_too_new', await cachedUserTooNew(cloudId, userId))) {
				return {
					isEligible: false,
					reason: 'user_is_too_new',
				};
			}

			return { isEligible: true, reason: '' };

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('eligibilityCheck', error, {
				cloudId,
				targetProduct: 'jira-product-discovery',
			});
			return { isEligible: false, reason: 'unexpected error' };
		}
	}, [cloudId, userId, createAnalyticsEvent, isJpdRoute, licensedProducts, withOverrides]);
};
